import { DAY_MS, VITALS, average, change, dayKey, percentile, visitorsOf, type StatEvent, type VitalName } from "./analytics";
import type { LeadRecord, PostRecord, ProjectRecord, ServiceRecord } from "../types";

// Everything the Statistics page shows, for a date range and optional filters (page, source, device, region), worked
// out from the events (reportFrom). No storage here: lib/stats-report.ts loads the data for the Next.js page, and the
// dashboards hub does the same in the browser with the admin API's files.

export const RANGES = [
  { value: "today", label: "Today", days: 1 },
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "90d", label: "3 months", days: 90 },
  { value: "12m", label: "12 months", days: 365 },
] as const;

export type Filters = { range: string; from: string; to: string; page: string; source: string; device: string; region: string };

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const plusDays = (day: string, n: number) => dayKey(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS);
const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

/** Reads the filters from the page address, with safe defaults (last 30 days, everything). */
export function parseFilters(params: Record<string, string | string[] | undefined>): Filters {
  const get = (key: string) => (typeof params[key] === "string" ? (params[key] as string).slice(0, 200) : "");
  const today = dayKey(Date.now());
  const oldest = plusDays(today, -365);
  // Custom dates (from/to in the address) win over the default; a named range otherwise.
  let range = get("range") || (get("from") || get("to") ? "custom" : "30d");
  let from: string;
  let to = today;
  const preset = RANGES.find((r) => r.value === range);
  if (preset) from = plusDays(today, -(preset.days - 1));
  else {
    range = "custom";
    from = DATE.test(get("from")) ? get("from") : plusDays(today, -29);
    to = DATE.test(get("to")) ? get("to") : today;
    if (from > to) [from, to] = [to, from];
    if (to > today) to = today;
    if (from < oldest) from = oldest;
  }
  return { range, from, to, page: get("page"), source: get("source"), device: get("device"), region: get("region") };
}

const matches = (f: Filters) => (e: StatEvent) =>
  (!f.page || e.p === f.page) && (!f.source || e.s === f.source) && (!f.device || e.d === f.device) && (!f.region || e.r === f.region);

/** Counts per key, biggest first. */
function tally<T>(items: T[], key: (item: T) => string | undefined) {
  const map = new Map<string, number>();
  for (const item of items) {
    const k = key(item);
    if (k) map.set(k, (map.get(k) ?? 0) + 1);
  }
  return [...map].sort((a, b) => b[1] - a[1]);
}

/** Unique visitors per key (e.g. per source), biggest first. */
function visitorsBy(events: StatEvent[], key: (e: StatEvent) => string) {
  const map = new Map<string, Set<string>>();
  for (const e of events) {
    if (e.e !== "view") continue;
    const k = key(e);
    if (!map.has(k)) map.set(k, new Set());
    map.get(k)!.add(`${dayKey(e.t)}|${e.v}`);
  }
  const viewCount = tally(events.filter((e) => e.e === "view"), key);
  const viewsOf = new Map(viewCount);
  return [...map].map(([k, set]) => ({ key: k, visitors: set.size, views: viewsOf.get(k) ?? 0 })).sort((a, b) => b.visitors - a.visitors);
}

function kpis(events: StatEvent[]) {
  const visitors = visitorsOf(events);
  const views = events.filter((e) => e.e === "view").length;
  const leads = events.filter((e) => e.e === "lead").length;
  const time = average(events.filter((e) => e.e === "time" && e.n !== undefined).map((e) => e.n!));
  return { visitors, views, leads, time, perVisitor: visitors ? views / visitors : 0, conversion: visitors ? (leads / visitors) * 100 : 0 };
}

/** The two periods a report compares: the chosen days and as many days just before them. */
export function periods(f: Filters) {
  const length = daysBetween(f.from, f.to);
  const prevTo = plusDays(f.from, -1);
  return { now: { from: f.from, to: f.to }, before: { from: plusDays(prevTo, -(length - 1)), to: prevTo } };
}

/** What a report is made from. Events: every event of each period (periods()), unfiltered. */
export type ReportData = {
  allNow: StatEvent[];
  allBefore: StatEvent[];
  posts: PostRecord[];
  services: ServiceRecord[];
  projects: ProjectRecord[];
  leads: LeadRecord[];
  since: string | null;
};

/**
 * Everything the Statistics page shows, from its data: no storage here (pure), so it runs wherever the data is (the
 * Next.js page through buildReport in stats-report.ts; the dashboards hub, in the browser).
 */
export function reportFrom(f: Filters, { allNow, allBefore, posts, services, projects, leads, since }: ReportData) {
  const length = daysBetween(f.from, f.to);
  const { from: prevFrom, to: prevTo } = periods(f).before;
  const keep = matches(f);
  const now = allNow.filter(keep);
  const before = allBefore.filter(keep);
  const k = kpis(now);
  const kb = kpis(before);

  // Readable names for pages.
  const titles = new Map<string, string>([
    ["/", "Home"],
    ["/services", "Services"],
    ["/projects", "Projects"],
    ["/about", "About"],
    ["/contact", "Contact"],
    ["/blog", "Blog"],
    ["/faq", "FAQ"],
  ]);
  posts.forEach((p) => titles.set(`/blog/${p.slug}`, p.title));
  services.forEach((s) => titles.set(`/services/${s.slug}`, s.name));
  const title = (path: string) => titles.get(path) ?? path;

  // Chart: hours for one day, days up to 3 months, weeks beyond.
  const unit: "hour" | "day" | "week" = length === 1 ? "hour" : length <= 92 ? "day" : "week";
  const buckets: { label: string; key: string; visitors: number; views: number }[] = [];
  const bucketOf = (t: number) => {
    if (unit === "hour") return new Date(t).toLocaleString("en-GB", { hour: "2-digit", hour12: false, timeZone: "Africa/Cairo" });
    const day = dayKey(t);
    if (unit === "day") return day;
    return plusDays(f.from, Math.floor((daysBetween(f.from, day) - 1) / 7) * 7);
  };
  if (unit === "hour") for (let h = 0; h < 24; h++) buckets.push({ key: String(h).padStart(2, "0"), label: `${String(h).padStart(2, "0")}:00`, visitors: 0, views: 0 });
  else for (let d = f.from; d <= f.to; d = plusDays(d, unit === "day" ? 1 : 7)) buckets.push({ key: d, label: d, visitors: 0, views: 0 });
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  const seen = new Map<string, Set<string>>();
  for (const e of now) {
    if (e.e !== "view") continue;
    const key = bucketOf(e.t);
    const bucket = byKey.get(key);
    if (!bucket) continue;
    bucket.views++;
    if (!seen.has(key)) seen.set(key, new Set());
    seen.get(key)!.add(`${dayKey(e.t)}|${e.v}`);
    bucket.visitors = seen.get(key)!.size;
  }

  // Pages.
  const views = now.filter((e) => e.e === "view");
  const pages = visitorsBy(now, (e) => e.p).map((row) => {
    const times = now.filter((e) => e.e === "time" && e.p === row.key && e.n !== undefined).map((e) => e.n!);
    return { path: row.key, title: title(row.key), views: row.views, visitors: row.visitors, time: average(times) };
  });

  // Reading: share of an article's visitors reaching each depth.
  const articles = pages
    .filter((p) => p.path.startsWith("/blog/"))
    .map((p) => {
      const readers = (m: number) => new Set(now.filter((e) => e.e === "read" && e.p === p.path && (e.n ?? 0) >= m).map((e) => `${dayKey(e.t)}|${e.v}`)).size;
      const pct = (m: number) => (p.visitors ? Math.round((readers(m) / p.visitors) * 100) : 0);
      return { ...p, r25: pct(25), r50: pct(50), r75: pct(75), r100: pct(100) };
    });

  // Page speed: the 75th percentile (Google's measure), overall and per device.
  const vitals = (Object.keys(VITALS) as VitalName[]).map((name) => {
    const values = (device?: string) => now.filter((e) => e.e === "vital" && e.k === name.toLowerCase() && (!device || e.d === device) && e.n !== undefined).map((e) => e.n!);
    const p75 = (device?: string) => percentile(values(device), 75);
    return { name, samples: values().length, all: p75(), phone: p75("phone"), tablet: p75("tablet"), desktop: p75("desktop") };
  });

  // Leads in the range (with the same filters).
  const fromT = Date.parse(`${f.from}T00:00:00Z`);
  const toT = Date.parse(`${f.to}T00:00:00Z`) + DAY_MS;
  const rangeLeads = leads.filter(
    (l) =>
      Date.parse(l.createdAt) >= fromT &&
      Date.parse(l.createdAt) < toT &&
      (!f.page || l.page === f.page) &&
      (!f.source || l.source === f.source) &&
      (!f.device || l.device === f.device) &&
      (!f.region || l.region === f.region),
  );

  // Content health (not tied to dates).
  const staleBefore = dayKey(Date.now() - 180 * DAY_MS);
  const health = [
    ...posts.filter((p) => p.status === "published" && (p.description.length < 120 || p.description.length > 160)).map((p) => ({ kind: "SEO", text: `“${p.title}”: Google description is ${p.description.length} characters (aim for 120–160).`, href: `/admin/blog/${p.id}` })),
    ...services.filter((s) => s.metaDescription.length < 120 || s.metaDescription.length > 160).map((s) => ({ kind: "SEO", text: `“${s.name}”: Google description is ${s.metaDescription.length} characters (aim for 120–160).`, href: `/admin/services/${s.id}` })),
    ...posts.filter((p) => (p.coverImage && !p.coverImage.alt) || p.body.some((b) => "image" in b && !b.image.alt)).map((p) => ({ kind: "Images", text: `“${p.title}” has an image without a description.`, href: `/admin/blog/${p.id}` })),
    ...projects.filter((p) => p.status === "published" && !p.image).map((p) => ({ kind: "Images", text: `Project “${p.name}” has no image yet.`, href: `/admin/projects/${p.id}` })),
    ...posts.filter((p) => p.status === "published" && p.updated < staleBefore).map((p) => ({ kind: "Freshness", text: `“${p.title}” hasn't been updated since ${p.updated}.`, href: `/admin/blog/${p.id}` })),
    ...posts.filter((p) => p.status === "draft").map((p) => ({ kind: "Drafts", text: `Draft waiting: “${p.title}”.`, href: `/admin/blog/${p.id}` })),
  ];

  return {
    since,
    length,
    unit,
    previous: { from: prevFrom, to: prevTo },
    kpis: [
      { label: "Visitors", value: k.visitors, change: change(k.visitors, kb.visitors), format: "number" as const },
      { label: "Page views", value: k.views, change: change(k.views, kb.views), format: "number" as const },
      { label: "Pages per visitor", value: k.perVisitor, change: change(k.perVisitor, kb.perVisitor), format: "decimal" as const },
      { label: "Time on a page", value: k.time ?? 0, change: k.time && kb.time ? change(k.time, kb.time) : null, format: "duration" as const },
      { label: "Leads", value: k.leads, change: change(k.leads, kb.leads), format: "number" as const },
      { label: "Conversion", value: k.conversion, change: change(k.conversion, kb.conversion), format: "percent" as const },
    ],
    buckets,
    pages,
    articles,
    sources: visitorsBy(now, (e) => e.s),
    devices: visitorsBy(now, (e) => e.d),
    regions: visitorsBy(now, (e) => e.r),
    clicks: tally(now.filter((e) => e.e === "click"), (e) => e.k),
    character: tally(now.filter((e) => e.e === "char"), (e) => e.k),
    vitals,
    notFound: tally(now.filter((e) => e.e === "404"), (e) => e.p),
    leads: { list: rangeLeads.slice(0, 5), byBudget: tally(rangeLeads, (l) => l.budget), total: rangeLeads.length },
    health,
    // Choices for the filters: whatever appears in the range (before filtering).
    options: {
      pages: tally(allNow.filter((e) => e.e === "view"), (e) => e.p).slice(0, 60).map(([path]) => ({ value: path, label: title(path) })),
      sources: tally(allNow, (e) => e.s).map(([v]) => v),
      devices: ["phone", "tablet", "desktop"],
      regions: tally(allNow, (e) => e.r).map(([v]) => v),
    },
    empty: !views.length,
  };
}

export type Report = ReturnType<typeof reportFrom>;
